/**
 * External Imports
 */
import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';

/**
 * External Imports
 */
import CSRNListTable from '../../../components/customTables/support/cscListTable';
import SearchHeader from '../../../components/searchHeader';
import SupportSideNav from '../../../components/sideNav/SupportSideNav';

import { RootState } from '../../../redux/store';
import { useGetSupportSubjectsToRoundQuery } from '../../../redux/apis/Users.api';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Employee,
  RoundHistory
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { SupportContainer } from '../styles/Support.styles';
import { date, onShiftFilter } from '../../../utils/helpers';

export interface CSRNListProps {
  authorizedUser?: AuthUserProps;
  selectedDeptsFromStore: string[];
  employeeShiftStatusFilter: string;
  isShiftStatusDataAvailable: boolean;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const CSRNList: FC<CSRNListProps> = ({
  authorizedUser,
  employeeShiftStatusFilter,
  isShiftStatusDataAvailable,
  selectedDeptsFromStore,
  searchTerm,
  setSearchTerm
}) => {
  const { csrnFilter = 'all' } = useParams();

  const {
    data: cscSubjectsToRoundData,
    // isError: isGetSupportSubjectsToRoundError,
    // isFetching: isGetSupportSubjectsToRoundFetching,
    isLoading: isGetSupportSubjectsToRoundLoading
  } = useGetSupportSubjectsToRoundQuery(
    {
      facilityId: authorizedUser?.facilityId ?? '',
      hcaid: authorizedUser?.hcaid ?? '',
      type: 'csc'
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !(authorizedUser?.hcaid && authorizedUser?.facilityId)
    }
  );

  const filteredCSCRounds = useMemo(() => {
    const isFilterOnShift = onShiftFilter(employeeShiftStatusFilter);
    let cscRoundsFilteredByDept =
      cscSubjectsToRoundData?.filter(employee => {
        const employeeDept =
          (employee.adHoc ? employee?.employeeDept : employee?.dept) ||
          'Unknown';
        return selectedDeptsFromStore.some(
          selectedDept => selectedDept === employeeDept
        );
      }) || [];

    if (employeeShiftStatusFilter && employeeShiftStatusFilter !== 'all') {
      cscRoundsFilteredByDept =
        cscRoundsFilteredByDept?.filter(
          employee => employee.onShift === isFilterOnShift
        ) || [];
    }

    return cscRoundsFilteredByDept.reduce(
      (
        acc: {
          cscSubjects: (Employee & RoundHistory)[];
          adHoc: (Employee & RoundHistory)[];
        },
        employee: Employee & RoundHistory
      ) =>
        employee.adHoc
          ? { ...acc, adHoc: [...acc.adHoc, employee] }
          : { ...acc, cscSubjects: [...acc.cscSubjects, employee] },
      { cscSubjects: [], adHoc: [] }
    );
  }, [
    cscSubjectsToRoundData,
    employeeShiftStatusFilter,
    selectedDeptsFromStore
  ]);

  const noRoundCount = useMemo(() => {
    return filteredCSCRounds.cscSubjects.filter(
      employee =>
        !employee?.lastRoundDate ||
        (employee?.lastRoundDate &&
          date.isOverInDays(employee.lastRoundDate, 30))
    ).length;
  }, [filteredCSCRounds]);

  const recentlyCompletedCount = useMemo(() => {
    return (
      filteredCSCRounds.cscSubjects.filter(
        employee =>
          employee?.lastRoundDate &&
          !date.isOverInWeeks(employee.lastRoundDate, 2)
      ).length +
      filteredCSCRounds.adHoc.filter(
        employee =>
          employee?.lastRoundDate &&
          !date.isOverInWeeks(employee.lastRoundDate, 2)
      ).length
    );
  }, [filteredCSCRounds]);

  const newNursesCount = useMemo(() => {
    return filteredCSCRounds.cscSubjects.filter(subject =>
      subject.subjectClassifications?.find(
        classification => classification === 'NewNurse'
      )
    ).length;
  }, [filteredCSCRounds]);

  const newToHCACount = useMemo(() => {
    return filteredCSCRounds.cscSubjects.filter(subject =>
      subject.subjectClassifications?.find(
        classification => classification === 'NewToHCA'
      )
    ).length;
  }, [filteredCSCRounds]);

  const newToDeptCount = useMemo(() => {
    return filteredCSCRounds.cscSubjects.filter(subject =>
      subject.subjectClassifications?.find(
        classification => classification === 'NewToDepartment'
      )
    ).length;
  }, [filteredCSCRounds]);

  return (
    <SupportContainer data-testid="csrn-support-view">
      <SupportSideNav
        type="csrn"
        total={filteredCSCRounds?.cscSubjects?.length ?? ''}
        noRoundCount={noRoundCount}
        recentlyCompletedCount={recentlyCompletedCount}
        newNursesCount={newNursesCount}
        newToHCACount={newToHCACount}
        newToDeptCount={newToDeptCount}
      />
      <div className="d-flex flex-column w-100">
        <SearchHeader
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isShiftStatusDataAvailable={isShiftStatusDataAvailable}
        />
        <CSRNListTable
          adhocRounds={filteredCSCRounds.adHoc}
          csrnFilter={csrnFilter}
          csrnRounds={filteredCSCRounds.cscSubjects}
          isLoading={isGetSupportSubjectsToRoundLoading}
          searchTerm={searchTerm}
        />
      </div>
    </SupportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    employeeShiftStatusFilter: state.EmployeeReducer.shiftStatusFilter,
    isShiftStatusDataAvailable:
      state.EmployeeReducer.isShiftStatusDataAvailable,
    selectedDeptsFromStore: state.EmployeeReducer.selectedDepts
  };
};

export default connect(mapReduxStateToProps)(CSRNList);
